import { KeyboardEventHandler, MutableRefObject, ReactNode } from 'react';
import { Glyphicon } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import {
  AsyncTypeAheadFieldInstance,
  AsyncTypeAheadFieldProps,
} from 'components/forms/typeAheadField/AsyncTypeAheadField.types';
import { SearchFieldAsyncTypeahead } from './FilterFormContextSearchField.styles';

interface FilterFormContextSearchFieldProps<T>
  extends Omit<
    AsyncTypeAheadFieldProps,
    'labelKey' | 'renderMenuItemChildren' | 'searchHandler'
  > {
  containerClassName?: string;
  instanceRef?: MutableRefObject<AsyncTypeAheadFieldInstance | undefined>;
  labelKey: keyof T | ((option: T) => string);
  name: string;
  renderMenuItemChildren: (option: T) => ReactNode;
  searchHandler: (query: string) => Promise<T[]>;
}

const FilterFormContextSearchField = <T,>({
  containerClassName,
  instanceRef,
  labelKey,
  name,
  renderMenuItemChildren,
  searchHandler,
  ...rest
}: FilterFormContextSearchFieldProps<T>) => {
  const { t } = useTranslation();

  const onKeyDownHandler: KeyboardEventHandler<HTMLInputElement> = (event) => {
    if (event.key === 'Enter') {
      const customOption = (event.target as HTMLElement)
        ?.closest('.form-field-container')
        ?.querySelector(
          '[role="option"].rbt-menu-custom-option',
        ) as HTMLElement;

      // selects the custom option defined by `allowNew` on hitting 'Enter', if there's one
      customOption?.click();
    }
  };

  return (
    <SearchFieldAsyncTypeahead
      allowNew={(options) => {
        if (options.length === 0) {
          return false;
        }
        return true;
      }}
      containerClassName={containerClassName}
      labelKey={labelKey as AsyncTypeAheadFieldProps['labelKey']}
      leftAddon={<Glyphicon glyph="search" />}
      name={name}
      onKeyDown={onKeyDownHandler}
      placeholder={t('common.search')}
      instanceRef={instanceRef}
      renderMenuItemChildren={
        renderMenuItemChildren as AsyncTypeAheadFieldProps['renderMenuItemChildren']
      }
      searchHandler={searchHandler as AsyncTypeAheadFieldProps['searchHandler']}
      {...rest}
    />
  );
};

export default FilterFormContextSearchField;
